import {
  Activity,
  Server,
  Wifi,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Camera,
  Search,
} from 'lucide-react';
import type { ComponentType } from 'react';

import { PageHeader } from '../components/common/PageHeader';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardContent } from '../components/ui/card';
import { useWebSocketContext } from '../contexts/WebSocketContext';
import { useDashboard } from '../hooks/useDashboard';

interface EmptyStateProps {
  title: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  actionLabel?: string;
  onAction?: () => void;
  searchQuery?: string;
  onClear?: () => void;
}

export function SystemOverviewPage() {
  const { counts, trends, isLoading, isConnected } = useDashboard();
  const { status } = useWebSocketContext();

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-[80vh] w-full'>
        <div className='flex flex-col items-center gap-4'>
          <RefreshCw className='h-10 w-10 animate-spin text-primary' />
          <p className='text-muted-foreground animate-pulse'>
            Loading dashboard data...
          </p>
        </div>
      </div>
    );
  }

  const totalAgents = counts?.total || 0;
  const onlineAgents = counts?.online || 0;

  const EmptyState = ({
    title,
    description,
    icon: Icon,
    actionLabel,
    onAction,
    searchQuery,
    onClear,
  }: EmptyStateProps) => (
    <div className='flex flex-col items-center justify-center py-12 px-4 text-center bg-gray-50/50 rounded-xl border border-dashed border-gray-200'>
      <div className='bg-white p-3 rounded-full shadow-sm mb-4'>
        <Icon className='h-8 w-8 text-gray-400' />
      </div>
      <h3 className='text-lg font-semibold text-gray-900 mb-1'>{title}</h3>
      <p className='text-sm text-gray-500 max-w-xs mb-6'>
        {searchQuery ? `No results matching "${searchQuery}"` : description}
      </p>
      <div className='flex gap-2'>
        {searchQuery ? (
          <Button variant='outline' size='sm' onClick={onClear}>
            Clear Search
          </Button>
        ) : (
          onAction && (
            <Button size='sm' onClick={onAction}>
              {actionLabel}
            </Button>
          )
        )}
      </div>
    </div>
  );

  return (
    <div className='container mx-auto py-8 px-4 space-y-8'>
      <PageHeader
        title='Dashboard'
        description='Monitor system health, agent connectivity, and recent activity.'
        secondaryActions={[
          {
            label: 'Refresh',
            icon: <RefreshCw className='h-4 w-4' />,
            onClick: () => window.location.reload(),
          },
        ]}
      />

      <div className='flex items-center gap-2'>
        <span
          className={`px-2 py-1 rounded text-[10px] uppercase tracking-wider font-bold border ${isConnected ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}
        >
          {isConnected ? 'Live' : 'Offline'}
        </span>
        <span className='text-xs text-gray-400'>System status: {status}</span>
      </div>

      {/* Stats Overview */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
        <Card className='overflow-hidden border-none shadow-sm bg-gradient-to-br from-white to-blue-50/30'>
          <CardHeader className='pb-2'>
            <div className='flex items-center justify-between'>
              <span className='text-xs font-bold text-gray-400 uppercase tracking-widest'>
                Total Agents
              </span>
              <div className='p-2 bg-blue-50 rounded-lg'>
                <Server className='h-4 w-4 text-blue-500' />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className='text-4xl font-black text-gray-900 tracking-tighter'>
              {totalAgents}
            </div>
            <div className='mt-2 flex items-center gap-1 text-xs'>
              {(trends?.total || 0) >= 0 ? (
                <TrendingUp className='h-3 w-3 text-green-500' />
              ) : (
                <TrendingDown className='h-3 w-3 text-red-500' />
              )}
              <span
                className={
                  (trends?.total || 0) >= 0
                    ? 'text-green-600 font-bold'
                    : 'text-red-600 font-bold'
                }
              >
                {(trends?.total || 0) > 0 ? '+' : ''}
                {trends?.total || 0}
              </span>
              <span className='text-gray-400 font-medium ml-1'>
                since last hour
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className='overflow-hidden border-none shadow-sm bg-gradient-to-br from-white to-green-50/30'>
          <CardHeader className='pb-2'>
            <div className='flex items-center justify-between'>
              <span className='text-xs font-bold text-gray-400 uppercase tracking-widest'>
                Online Status
              </span>
              <div className='p-2 bg-green-50 rounded-lg'>
                <Wifi className='h-4 w-4 text-green-500' />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className='text-4xl font-black text-green-600 tracking-tighter'>
              {onlineAgents}
            </div>
            <div className='mt-4 w-full bg-gray-100 rounded-full h-2 overflow-hidden'>
              <div
                className='bg-green-500 h-full transition-all duration-1000 ease-out'
                style={{
                  width: `${totalAgents > 0 ? (onlineAgents / totalAgents) * 100 : 0}%`,
                }}
              />
            </div>
            <div className='mt-2 text-[10px] font-bold text-gray-400 uppercase tracking-wider'>
              {totalAgents > 0
                ? Math.round((onlineAgents / totalAgents) * 100)
                : 0}
              % Operational
            </div>
          </CardContent>
        </Card>

        <Card className='overflow-hidden border-none shadow-sm bg-gradient-to-br from-white to-purple-50/30'>
          <CardHeader className='pb-2'>
            <div className='flex items-center justify-between'>
              <span className='text-xs font-bold text-gray-400 uppercase tracking-widest'>
                Cameras
              </span>
              <div className='p-2 bg-purple-50 rounded-lg'>
                <Camera className='h-4 w-4 text-purple-500' />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className='text-4xl font-black text-purple-600 tracking-tighter'>
              0
            </div>
            <div className='mt-2 text-xs text-gray-400 font-medium'>
              No cameras registered
            </div>
          </CardContent>
        </Card>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
        {/* Recent Agents Section */}
        <div className='space-y-4'>
          <div className='flex items-center justify-between'>
            <h2 className='text-lg font-bold flex items-center gap-2'>
              <Activity className='h-5 w-5 text-blue-500' />
              Recent Activity
            </h2>
            <Button
              variant='ghost'
              size='sm'
              className='text-blue-600 font-bold text-xs'
              onClick={() => (window.location.hash = '/agents')}
            >
              View All
            </Button>
          </div>

          <EmptyState
            title='No agents connected'
            description='Start by registering an agent to see activity here.'
            icon={Server}
            actionLabel='Register Agent'
            onAction={() => (window.location.hash = '/agents')}
          />
        </div>

        {/* System Health / Discovered Devices */}
        <div className='space-y-4'>
          <div className='flex items-center justify-between'>
            <h2 className='text-lg font-bold flex items-center gap-2'>
              <Search className='h-5 w-5 text-purple-500' />
              Discovered Devices
            </h2>
            <Button
              variant='ghost'
              size='sm'
              className='text-purple-600 font-bold text-xs'
              onClick={() => (window.location.hash = '/cameras?tab=discovered')}
            >
              Manage
            </Button>
          </div>

          <EmptyState
            title='No devices discovered'
            description='Scan your network from an agent to find new devices.'
            icon={Search}
            actionLabel='Start Scan'
            onAction={() => (window.location.hash = '/cameras?tab=discovered')}
          />
        </div>
      </div>

      {/* Info Banner */}
      <div className='p-4 bg-blue-600 rounded-2xl shadow-lg shadow-blue-200 flex flex-col md:flex-row items-center justify-between gap-4 text-white'>
        <div className='flex items-center gap-4'>
          <div className='p-3 bg-white/20 rounded-xl backdrop-blur-sm'>
            <Activity className='h-6 w-6' />
          </div>
          <div>
            <h3 className='font-bold leading-tight'>Real-time Engine Active</h3>
            <p className='text-blue-100 text-xs font-medium opacity-90'>
              The dashboard is now connected to the real-time data stream.
            </p>
          </div>
        </div>
        <Button
          variant='secondary'
          size='sm'
          className='bg-white text-blue-600 font-bold border-none hover:bg-blue-50'
          onClick={() => (window.location.hash = '/settings')}
        >
          System Settings
        </Button>
      </div>
    </div>
  );
}

export default SystemOverviewPage;
